import { useEntitiesState } from "../../hooks/useEntitiesState";
import { useDoc } from "@poseidon/firebase/hooks/useDoc";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { getSchemaErrors } from "@poseidon/utils/yup";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";

const childDefaults = () => ({
  rate: "",
  fromMonths: "",
  toMonths: "",
});

const rowDefaults = (getId) => ({
  toAmount: "",
  fromAmount: "",
  rows: [
    {
      id: getId(),
      rate: "",
      fromMonths: "",
      toMonths: "",
    },
  ],
});

const sorter = (rows) => {
  return [...rows]
    .sort((a, b) => parseInt(a.toAmount) - parseInt(b.toAmount))
    .map((row, idx, all) => {
      const toAmount = idx > 0 && all[idx - 1].toAmount;
      const fromAmount =
        idx === 0 ? "0" : toAmount ? parseInt(toAmount) + 1 : "";
      return {
        ...row,
        rows: [...row.rows].sort((a, b) => a.fromMonths - b.fromMonths),
        fromAmount,
      };
    });
};

const mapForSave = (rows) => {
  return rows.map(({ id, rows, ...row }) => {
    return {
      fromAmount: parseInt(row.fromAmount) ?? 0,
      toAmount: parseInt(row.toAmount) ?? 0,
      rates: rows.map(({ id, ...row }) => ({
        fromMonths: parseInt(row.fromMonths) ?? 0,
        toMonths: parseInt(row.toMonths) ?? 0,
        rate: parseFloat(row.rate) ?? 0,
      })),
    };
  });
};

const rowSchema = Yup.object().shape({
  fromAmount: Yup.number()
    .typeError("Invalid number")
    .required()
    .min(0, "Must be 0 or more")
    .lessThan(Yup.ref("toAmount"), 'Must be less than "To Amount"'),
  toAmount: Yup.number()
    .typeError("Invalid number")
    .required()
    .positive("Must be 0 or more")
    .moreThan(Yup.ref("fromAmount"), 'Must be more than "From Amount"'),
});
const childSchema = Yup.object().shape({
  fromMonths: Yup.number()
    .typeError("Invalid number")
    .required()
    .min(1, "Must be 1 or more")
    .lessThan(Yup.ref("toMonths"), 'Must be less than "To Months"'),
  toMonths: Yup.number()
    .typeError("Invalid number")
    .required()
    .min(1, "Must be 1 or more")
    .moreThan(Yup.ref("fromMonths"), 'Must be more than "From Months"'),
  rate: Yup.number()
    .typeError("Invalid number")
    .required()
    .min(0.1, "Must be at least 0.1%")
    .max(100, "Must be no more than 100%"),
});

const childCrossover = (child, sibiling) => {
  const cFrom = parseInt(child.fromMonths);
  const cTo = parseInt(child.toMonths);
  const sFrom = parseInt(sibiling.fromMonths);
  const sTo = parseInt(sibiling.toMonths);

  if (cFrom >= sFrom && cFrom <= sTo) {
    return true;
  }
  return cTo >= sFrom && cTo <= sTo;
};
const validate = (rows) => {
  const errors = rows
    .map((row) => {
      const childErrors = row.rows
        .map((child) => {
          const crossover = row.rows.find((sibling) => {
            return sibling.id !== child.id && childCrossover(child, sibling);
          });
          const schemaErrors = getSchemaErrors(childSchema, child);
          const crossoverError = crossover
            ? "Conflicts with another row"
            : undefined;
          if (!schemaErrors && !crossoverError) {
            return undefined;
          }
          return {
            id: child.id,
            fieldErrors: schemaErrors,
            message: crossoverError,
          };
        })
        .filter((v) => !!v);
      const schemaErrors = getSchemaErrors(rowSchema, row);
      return [
        ...(schemaErrors
          ? [
              {
                id: row.id,
                fieldErrors: schemaErrors,
              },
            ]
          : []),
        ...childErrors,
      ];
    })
    .flat();
  return errors.length ? errors : undefined;
};

export const useRateMatrixState = () => {
  const { setToast } = useToastMessage();
  const { setError } = useErrorMessage();
  const { data, loading, loadError, saveError, saving, update } = useDoc(
    "matrix",
    "rates"
  );
  const { rows, actions } = useEntitiesState({
    nested: true,
    defaults: rowDefaults,
    childDefaults,
    sorter,
  });
  const [validationErrors, setValidationErrors] = useState();
  useEffect(() => {
    if (data) {
      actions.init(
        data.rules.map(({ rates, ...row }) => ({ ...row, rows: rates }))
      );
    }
  }, [actions, data]);
  const handleSave = () => {
    const vErr = validate(rows);
    if (!vErr) {
      setValidationErrors(undefined);
      update({ rules: mapForSave(rows) }).then((success) => {
        if (success) {
          setToast("Successfully saved rate matrix");
        } else {
          setError("Error saving rate matrix");
        }
      });
    } else {
      setValidationErrors(vErr);
    }
  };
  return {
    rows,
    actions,
    loading,
    loadError,
    saveError,
    saving,
    handleSave,
    validationErrors,
  };
};
